import React, { useState, useEffect, useCallback } from 'react';
import { Icon, Button, Input, message, Table } from 'antd';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import { withTranslation } from 'react-i18next';
import { Header, DataContainer, FlexDiv } from '../../../MailRoom/styles';
import { getWeek } from '../../../../utils/javascript';
import HybridQueueFilters from './HybridQueueFilters';
import { DocListOverlay, CloseIcon, ContentContainer, DocListFooter } from './HybridMailStyles';
import { getHybridQueueList, createQueueBatch, viewFile } from '../../../../utils/services/hybridMailAPI';
import { useHybridMail } from './HybridMailContext';
import { SEARCHABLE_TABLE } from '../TranslationValues';
import { base64ToPdfBlob } from '../../../../utils/helper';
import { HYBRID_MAIL_ROOM } from './TranslationValue';

const NAMESPACES = ['HYBRID_MAIL_ROOM'];

const HybridQueue = ({
  jobName,
  setHybridInfo,
  setCurrentPage,
  closeModals,
  t,
}) => {
  const {
    qSearchValue,
    qStartAndEndDate,
    setQStartAndEndDate,
  } = useHybridMail();

  const [isLoading, setIsLoading] = useState(false);
  const [createBatch, setCreateBatch] = useState(false);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [qCurrentPage, setQCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [viewLoadingId, setViewLoadingId] = useState(null);

  const setDefaultWeek = useCallback(() => {
    const startAndEndDate = getWeek();
    setQStartAndEndDate({
      startDate: startAndEndDate.startDate,
      endDate: startAndEndDate.endDate,
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    return;
  }, []);

  const escFunction = useCallback(
    event => {
      if (event.key === 'Escape') {
        clearAllFilters();
        setCreateBatch(false);
        setDefaultWeek();
      }
    },
    [setCreateBatch, setDefaultWeek, clearAllFilters],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);
    return () => document.removeEventListener('keydown', escFunction, false);
  }, [escFunction]);

  useEffect(() => {
    setDefaultWeek();
  }, [setDefaultWeek]);

  const fetchQueueList = useCallback(async () => {
    if (!qStartAndEndDate?.startDate || !qStartAndEndDate?.endDate) return;
    setIsLoading(true);
    try {
      const params = {
        uploadedStartDate: qStartAndEndDate?.startDate,
        uploadedEndDate: qStartAndEndDate?.endDate,
        fileName: qSearchValue ? qSearchValue : '',
        page: qCurrentPage,
        limit: pageSize,
        excludeAddedToBatch: true,
      };
      const response = await getHybridQueueList(params);
      if (response?.data && response?.statusCode === 7024) {
        setData(response?.data || []);
        setDataCount(response?.dataCount || 0);
      } else {
        message.error(t(HYBRID_MAIL_ROOM.FETCH_RECORDS_FAIL.key, HYBRID_MAIL_ROOM.FETCH_RECORDS_FAIL.value));
      }
    } catch (error) {
      console.error('Error fetching hybrid queue list:', error);
        message.error(t(HYBRID_MAIL_ROOM.FETCH_RECORDS_FAIL.key, HYBRID_MAIL_ROOM.FETCH_RECORDS_FAIL.value));
    } finally {
      setIsLoading(false);
    }
  }, [qStartAndEndDate, qCurrentPage, pageSize, qSearchValue]);

  useEffect(() => {
    fetchQueueList();
  }, [fetchQueueList, qStartAndEndDate, qSearchValue]);

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleFileView = async(record)=>{
    setIsViewLoading(true);
    setViewLoadingId(record?.id);
    try {
      const response = await viewFile(record?.id);
      if (response?.status === 200 && response?.data?.base64Content) {
        const blob = base64ToPdfBlob(response.data.base64Content);
        const url = URL.createObjectURL(blob);
        const newTab = window.open();
        const doc = newTab.document;
        doc.title = t(SEARCHABLE_TABLE.LOADING_PDF.key, SEARCHABLE_TABLE.LOADING_PDF.value);
        const container = doc.createElement('div');
        container.style.display = 'flex';
        container.style.justifyContent = 'center';
        container.style.alignItems = 'center';
        container.style.height = '100vh';
        container.style.fontFamily = 'sans-serif';
        container.innerHTML = `
          <div style="text-align:center">
            <h2>${t(SEARCHABLE_TABLE.LOADING_DOCUMENT.key, SEARCHABLE_TABLE.LOADING_DOCUMENT.value)}</h2>
            <p>${t(SEARCHABLE_TABLE.PLEASE_WAIT.key, SEARCHABLE_TABLE.PLEASE_WAIT.value)}</p>
          </div>
        `;
        doc.body.appendChild(container);
        newTab.location.href = url;
      }
      else {
        message.error(t(HYBRID_MAIL_ROOM.UNABLE_TO_VIEW.key, HYBRID_MAIL_ROOM.UNABLE_TO_VIEW.value));
      }
    } catch (error) {
      message.error(t(HYBRID_MAIL_ROOM.UNABLE_TO_VIEW.key, HYBRID_MAIL_ROOM.UNABLE_TO_VIEW.value));
    }
    finally {
      setIsViewLoading(false);
    }
  }

  const handleCreateBatch = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      setIsLoading(true);
      const payload = {
        queueIds: selectedRowKeys,
        batchName: jobName,
        envelopeType:'C5',
        deliveryClass:'First class'
      };
      const response = await createQueueBatch(payload);
      if (response?.data && response?.statusCode === 7026) {
        message.success(t(HYBRID_MAIL_ROOM.CREATE_SUCCESS_MESSAGE.key, HYBRID_MAIL_ROOM.CREATE_SUCCESS_MESSAGE.value));
        setHybridInfo({
          batchName: jobName,
          splitOnCriteria: 'address',
          pageCount: null,
          uploadedFile: null,
          hybridBatchId: response?.data?.batchId,
          batchInfo: {},
        });
        setCurrentPage('dashboard');
        closeModals(false, false);
        setSelectedRowKeys([]);
      } else {
        message.error(t(HYBRID_MAIL_ROOM.CREARE_FAILURE_MESSAGE.key, HYBRID_MAIL_ROOM.CREARE_FAILURE_MESSAGE.value));
      }
    } catch (error) {
      console.error('Error creating batch:', error);
        message.error(t(HYBRID_MAIL_ROOM.CREARE_FAILURE_MESSAGE.key, HYBRID_MAIL_ROOM.CREARE_FAILURE_MESSAGE.value));
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'original_file_name',
      key: 'original_file_name',
    },
    {
      title: 'Uploaded At',
      dataIndex: 'uploaded_at',
      key: 'uploaded_at',
    },
    {
      title: 'View File',
      dataIndex: 'file_type',
      width: '20%',
      align: 'center',
      onCell: () => ({
        'data-no-select': true,        
        onClick: e => e.stopPropagation(),
      }),
      render: (text, record) => (
        <Button
          style={{ outline: 'none', border: 'none', background: 'none', backgroundColor: 'none' }}
          className="failure_reason"
          onClick={() => handleFileView(record)}
        >
         {isViewLoading && record.id === viewLoadingId ? <Icon type="loading" /> : <Icon type="eye" />}
        </Button>
      ),
      }
  ];

  const handlePageChange = (page, pageSize) => {
    setQCurrentPage(page);
    setPageSize(pageSize);
  };

  const onRow = record => ({
    onClick: (e) => {
      if (e.target.closest('td')?.dataset.noSelect) return;
      const key = record.id;
      const isSelected = selectedRowKeys.includes(key);
      setSelectedRowKeys(
        isSelected ? selectedRowKeys.filter(k => k !== key) : [...selectedRowKeys, key],
      );
    },
    style: { cursor: 'pointer' },
  });

  return (
    <DocListOverlay>
      <Header isLoading={isLoading} padding="1.5rem" height="20%" justifyContent={'space-between'}>
        <div className="batchInputs">
          <h1>{t(HYBRID_MAIL_ROOM.BATCH_QUEUE_TITLE.key, HYBRID_MAIL_ROOM.BATCH_QUEUE_TITLE.value)}</h1>
        </div>
        <FlexDiv isLoading={isLoading}>
          <HybridQueueFilters />
          <CloseIcon>
            <Icon
              type="close"
              onClick={() => {
                clearAllFilters();
                setCreateBatch(false);
                setDefaultWeek();
                closeModals(false,true,jobName);
              }}
            />
          </CloseIcon>
        </FlexDiv>
      </Header>

      <ContentContainer height={'70%'}>
        <DataContainer width={'100%'} height={'70%'}>
          <SwitchTransition>
            <CSSTransition
              key={1}
              addEndListener={(node, done) => node.addEventListener('transitionend', done, false)}
              classNames="fade"
            >
              <Table
                size="middle"
                className="hybrid-queue-table"
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={isLoading}
                onRow={onRow}
                pagination={{
                  current: qCurrentPage,
                  pageSize: pageSize,
                  total: dataCount,
                  onChange: handlePageChange,
                  showSizeChanger: true,
                  onShowSizeChange: handlePageChange,
                }}
              />
            </CSSTransition>
          </SwitchTransition>
        </DataContainer>
      </ContentContainer>

      <DocListFooter
        width={'100%'}
        justifyContent={'space-between'}
        isLoading={isLoading}
        height={'10%'}
      >
        <Button
          type="primary"
          disabled={selectedRowKeys.length === 0}
          onClick={handleCreateBatch}
          loading={isLoading}
        >
        {t(HYBRID_MAIL_ROOM.CREATE_BATCH.key, HYBRID_MAIL_ROOM.CREATE_BATCH.value)} 
         </Button>
      </DocListFooter>
    </DocListOverlay>
  );
};

export default withTranslation(NAMESPACES)(HybridQueue);
